import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button, ButtonGroup, styled, Menu, MenuItem, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';

const StyledButtonGroup = styled(ButtonGroup)`
  margin: 0 16px;
  display: flex;
  align-items: center;
  .MuiButton-root {
    color: ${props => props.theme.header.color};
    border-color: ${props => props.theme.header.color};
    &.active {
      background-color: ${props => props.theme.palette.primary.main};
      border-color: ${props => props.theme.palette.primary.main};
    }
    &:hover {
      background-color: rgba(255, 255, 255, 0.1);
    }
    min-width: 40px;
    padding: 6px 16px;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    .MuiSvgIcon-root {
      font-size: 1.25rem;
    }
  }
`;

const NavbarTabs = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  // Fonction utilitaire pour le logging
  const logDebug = (functionName, message, data = {}) => {
    console.log(`[NavbarTabs][${functionName}] ${message}`, data);
  };

  // Initialisation avec la route actuelle
  const [tabs, setTabs] = useState([{ 
    id: '1', 
    name: 'Page', 
    route: location.pathname + location.search
  }]);

  const [activePage, setActivePage] = useState('1');

  // État pour le menu contextuel et le dialogue
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedTab, setSelectedTab] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editedTabName, setEditedTabName] = useState('');

  const endsWithId = (path) => {
    if (!path) return false;
    const segments = path.split('/');
    const lastSegment = segments[segments.length - 1];
    return /^\d+$/.test(lastSegment);
  };

  const extractId = (path) => {
    if (!path) return null;
    const segments = path.split('/');
    const lastSegment = segments[segments.length - 1];
    return /^\d+$/.test(lastSegment) ? lastSegment : null;
  };

  const getPageType = (path) => {
    if (!path) return null;
    const segments = path.split('/');
    // On veut le chemin complet jusqu'à edit s'il existe
    if (segments.length >= 2) {
      const baseType = segments.slice(1, 3).join('/'); // gestion/devis ou gestion/commande
      if (segments[3] === 'edit') {
        return baseType + '/edit';
      }
      return baseType;
    }
    return null;
  };

  const isSameRouteType = (route1, route2) => {
    const segments1 = route1.split('/');
    const segments2 = route2.split('/');

    // On compare les chemins jusqu'à edit
    const path1 = segments1.slice(1, segments1[3] === 'edit' ? 4 : 3).join('/');
    const path2 = segments2.slice(1, segments2[3] === 'edit' ? 4 : 3).join('/');

    logDebug('isSameRouteType', 'Comparaison des chemins', {
      route1, route2,
      path1, path2
    });

    return path1 === path2;
  };

  const navigateToRoute = (route) => {
    logDebug('navigateToRoute', 'Tentative de navigation', {
      route,
      currentPath: location.pathname + location.search
    });

    if (location.pathname + location.search === route) {
      logDebug('navigateToRoute', 'Navigation ignorée - même route');
      return;
    }

    logDebug('navigateToRoute', 'Navigation vers', { route });
    navigate(route, { replace: false });

    if (endsWithId(route)) {
      const id = extractId(route);
      logDebug('navigateToRoute', 'Déclenchement forceReload', { id });
      window.dispatchEvent(new CustomEvent('forceReload', { detail: { id } }));
    }
  };

  useEffect(() => {
    const currentFullPath = location.pathname + location.search;
    const activeTab = tabs.find(tab => tab.id === activePage);
    
    logDebug('useEffect[location]', 'Vérification mise à jour route', {
      currentFullPath,
      activeTab,
      allTabs: tabs,
      currentLocation: location
    });

    if (!activeTab) {
      logDebug('useEffect[location]', 'Pas d\'onglet actif trouvé');
      return;
    }

    if (activeTab.route === currentFullPath) {
      logDebug('useEffect[location]', 'Route déjà à jour');
      return;
    }

    const currentType = getPageType(currentFullPath);
    const activeTabType = getPageType(activeTab.route);

    if (!isSameRouteType(currentFullPath, activeTab.route)) {
      logDebug('useEffect[location]', 'Types de routes différents - pas de mise à jour', {
        currentType,
        activeTabType,
        currentPath: currentFullPath,
        savedRoute: activeTab.route
      });
      return;
    }

    logDebug('useEffect[location]', 'Mise à jour des onglets', {
      previousRoute: activeTab.route,
      newRoute: currentFullPath,
      currentType,
      activeTabType
    });

    // Créer une copie mise à jour des onglets
    const updatedTabs = tabs.map(tab => 
      tab.id === activePage ? { ...tab, route: currentFullPath } : tab
    );

    setTabs(updatedTabs);
  }, [location.pathname, location.search, activePage]);

  const handlePageClick = (tabId) => {
    logDebug('handlePageClick', 'Clic sur onglet', { tabId, activePage });

    if (tabId === activePage) {
      logDebug('handlePageClick', 'Clic ignoré - même onglet');
      return;
    }

    const targetTab = tabs.find(tab => tab.id === tabId);
    if (!targetTab?.route) {
      logDebug('handlePageClick', 'Onglet cible non trouvé ou sans route');
      return;
    }

    const currentFullPath = location.pathname + location.search;
    const currentTab = tabs.find(tab => tab.id === activePage);

    logDebug('handlePageClick', 'État actuel', {
      currentPath: currentFullPath,
      currentType: getPageType(currentFullPath),
      targetRoute: targetTab.route,
      targetType: getPageType(targetTab.route),
      allTabs: tabs
    });

    // D'abord, naviguer vers la nouvelle route
    logDebug('handlePageClick', 'Navigation vers', {
      route: targetTab.route
    });
    navigateToRoute(targetTab.route);

    // Ensuite, mettre à jour l'onglet actif
    setActivePage(tabId);

    // Enfin, sauvegarder l'état actuel dans l'ancien onglet
    if (currentTab) {
      const updatedTabs = tabs.map(tab => {
        if (tab.id === activePage) {
          logDebug('handlePageClick', 'Sauvegarde route dans ancien onglet', {
            tabId: tab.id,
            oldRoute: tab.route,
            newRoute: currentFullPath
          });
          return { ...tab, route: currentFullPath };
        }
        return tab;
      });
      setTabs(updatedTabs);
    }
  };

  const handleEditTab = () => {
    if (!selectedTab) return;
    setEditedTabName(selectedTab.name);
    setIsEditDialogOpen(true);
    setContextMenu(null);
  };

  const handleDeleteTab = () => {
    if (!selectedTab || tabs.length <= 1) return;

    const currentIndex = tabs.findIndex(tab => tab.id === selectedTab.id);
    const updatedTabs = tabs.filter(tab => tab.id !== selectedTab.id);
    
    if (activePage === selectedTab.id) {
      const newIndex = currentIndex === tabs.length - 1 ? currentIndex - 1 : currentIndex;
      const newActiveTab = updatedTabs[newIndex];
      
      setTabs(updatedTabs);
      setActivePage(newActiveTab.id);
      navigateToRoute(newActiveTab.route);
    } else {
      setTabs(updatedTabs);
    }
    
    handleCloseContextMenu();
  };

  const handleAddTab = () => {
    if (tabs.length >= 5) return;

    const newId = String(Math.max(...tabs.map(tab => parseInt(tab.id))) + 1);
    const newTab = {
      id: newId,
      name: 'Page',
      route: '/'
    };
    
    const finalTabs = [...tabs, newTab];
    
    setTabs(finalTabs);
    setActivePage(newId);
    navigate('/');
    
    setSelectedTab(newTab);
    setEditedTabName('Page');
    setIsEditDialogOpen(true);
  };

  const handleSaveEdit = () => {
    if (!selectedTab || !editedTabName.trim()) {
      handleCloseDialog();
      return;
    }

    const updatedTabs = tabs.map(tab =>
      tab.id === selectedTab.id ? { ...tab, name: editedTabName.trim() } : tab
    );

    setTabs(updatedTabs);
    handleCloseDialog();
  };

  const handleContextMenu = (event, tab) => {
    event.preventDefault();
    setContextMenu({ mouseX: event.clientX - 2, mouseY: event.clientY - 4 });
    setSelectedTab(tab);
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
    setSelectedTab(null);
  };

  const handleCloseDialog = () => {
    setIsEditDialogOpen(false);
    setSelectedTab(null);
    setEditedTabName('');
    setContextMenu(null);
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <StyledButtonGroup variant="outlined" size="small">
        {tabs.map(tab => (
          <Button
            key={tab.id}
            className={activePage === tab.id ? 'active' : ''}
            onClick={() => handlePageClick(tab.id)}
            onContextMenu={(e) => handleContextMenu(e, tab)}
          >
            {tab.name}
          </Button>
        ))}
        {tabs.length < 5 && (
          <Button 
            onClick={handleAddTab}
            style={{ padding: '6px 12px' }}
            onContextMenu={(e) => {
              e.preventDefault();
            }}
          >
            <AddIcon />
          </Button>
        )}
      </StyledButtonGroup>

      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={handleEditTab}>{t("Renommer")}</MenuItem>
        {tabs.length > 1 && (
          <MenuItem onClick={handleDeleteTab}>{t("Supprimer")}</MenuItem>
        )}
      </Menu>

      <Dialog open={isEditDialogOpen} onClose={handleCloseDialog}>
        <DialogTitle>{t("Renommer l'onglet")}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t("Nom de l'onglet")}
            type="text"
            fullWidth
            value={editedTabName}
            onChange={(e) => setEditedTabName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSaveEdit();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t("Annuler")}</Button>
          <Button onClick={handleSaveEdit} color="primary">
            {t("Enregistrer")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default NavbarTabs; 